import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { environmentURL } from '../../environments/environments'

@Injectable({
  providedIn: 'root'
})
export class InactivityTimeoutService {
  private timeoutDuration = environmentURL.sessionTimeOut // Tiempo de timeout configurado en environment
  private timeoutId: any
  private isActive = new BehaviorSubject<boolean>(true)
  private lastActivityTime: number = Date.now()
  private readonly ACTIVITY_KEY = 'lastActivityTime'
  /** Flag multi-pestaña: 'true' = se requiere reautenticación (no cierra sesión). */
  private readonly REAUTH_KEY = 'sessionReauth'
  private reauthRequired$ = new BehaviorSubject<boolean>(false)
  private listenersBound = false
  private boundActivityHandler = () => this.onUserActivity()

  // Eventos que resetean el timeout
  private resetEvents = [
    'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'
  ]

  constructor() {
    this.setupStorageListener()
  }

  /**
   * Emite true cuando hay que mostrar el diálogo de reautenticación.
   */
  getReauthRequired(): Observable<boolean> {
    return this.reauthRequired$.asObservable()
  }

  isReauthPending(): boolean {
    return this.reauthRequired$.value
  }

  /**
   * Configura el listener para sincronizar entre pestañas
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      // Logout en otra pestaña: token ya no está en localStorage compartido
      if (event.key === 'token' && event.oldValue && !event.newValue) {
        localStorage.removeItem(this.REAUTH_KEY)
        localStorage.removeItem(this.ACTIVITY_KEY)
        this.clearReauthFlagLocal()
        this.stopInactivityTimer()
        return
      }

      if (event.key === this.ACTIVITY_KEY && event.newValue) {
        // Otra pestaña actualizó la actividad, reinicia el timer (si no hay reauth pendiente)
        if (!this.isReauthPending()) {
          this.lastActivityTime = parseInt(event.newValue, 10)
          this.resetTimer()
        }
      } else if (event.key === this.REAUTH_KEY) {
        if (event.newValue === 'true') {
          this.enterReauthMode(false)
        } else if (event.newValue === null || event.newValue === '') {
          // Otra pestaña reautenticó (token sigue presente) o limpió el flag
          if (this.isReauthPending()) {
            this.clearReauthFlagLocal()
            if (localStorage.getItem('token')) {
              this.startInactivityTimer()
            }
          }
        }
      }
    })
  }

  /**
   * Inicia el monitoreo de inactividad del usuario
   */
  startInactivityTimer(): void {
    if (this.isReauthPending()) {
      return
    }
    this.updateActivityTime()
    this.resetTimer()
    this.addEventListeners()
  }

  /**
   * Inicializa el servicio verificando si hay una sesión activa
   * y sincroniza con otras pestañas
   */
  initializeService(): void {
    const token = localStorage.getItem('token')
    if (!token) {
      return
    }

    // Si otra pestaña (o esta) dejó reauth pendiente, mostrar diálogo
    if (localStorage.getItem(this.REAUTH_KEY) === 'true') {
      this.enterReauthMode(false)
      return
    }

    const storedActivityTime = localStorage.getItem(this.ACTIVITY_KEY)
    if (storedActivityTime) {
      this.lastActivityTime = parseInt(storedActivityTime, 10)
      const timeSinceLastActivity = Date.now() - this.lastActivityTime

      if (timeSinceLastActivity >= this.timeoutDuration) {
        this.handleInactivity()
        return
      }
    }

    this.startInactivityTimer()
  }

  /**
   * Detiene el monitoreo de inactividad
   */
  stopInactivityTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    this.removeEventListeners()
  }

  /**
   * Actualiza el tiempo de actividad en localStorage para sincronizar entre pestañas
   */
  private updateActivityTime(): void {
    this.lastActivityTime = Date.now()
    localStorage.setItem(this.ACTIVITY_KEY, this.lastActivityTime.toString())
  }

  private onUserActivity(): void {
    if (this.isReauthPending()) {
      return
    }
    this.updateActivityTime()
    this.resetTimer()
  }

  /**
   * Resetea el timer de inactividad
   */
  resetTimer(): void {
    if (this.isReauthPending()) {
      return
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }

    const timeSinceLastActivity = Date.now() - this.lastActivityTime
    const remainingTime = Math.max(0, this.timeoutDuration - timeSinceLastActivity)

    this.timeoutId = setTimeout(() => {
      this.handleInactivity()
    }, remainingTime)
  }

  /**
   * Maneja la inactividad: pide reauth sin borrar sesión ni navegar a login.
   */
  private handleInactivity(): void {
    this.enterReauthMode(true)
  }

  /**
   * Entra en modo reautenticación. Si broadcast=true, notifica a otras pestañas.
   */
  private enterReauthMode(broadcast: boolean): void {
    this.stopInactivityTimer()
    if (broadcast) {
      localStorage.setItem(this.REAUTH_KEY, 'true')
    }
    this.reauthRequired$.next(true)
  }

  private clearReauthFlagLocal(): void {
    this.reauthRequired$.next(false)
  }

  /**
   * Tras reauth exitosa: limpia flag, reinicia timer y sincroniza pestañas.
   */
  completeReauth(): void {
    localStorage.removeItem(this.REAUTH_KEY)
    this.clearReauthFlagLocal()
    this.startInactivityTimer()
  }

  /**
   * Agrega los event listeners para detectar actividad del usuario
   */
  private addEventListeners(): void {
    if (this.listenersBound) {
      return
    }
    this.resetEvents.forEach(event => {
      document.addEventListener(event, this.boundActivityHandler, true)
    })
    this.listenersBound = true
  }

  /**
   * Remueve los event listeners
   */
  private removeEventListeners(): void {
    if (!this.listenersBound) {
      return
    }
    this.resetEvents.forEach(event => {
      document.removeEventListener(event, this.boundActivityHandler, true)
    })
    this.listenersBound = false
  }

  /**
   * Limpia los datos de timeout del localStorage
   * Se usa cuando se cierra sesión manualmente
   */
  clearTimeoutData(): void {
    localStorage.removeItem(this.ACTIVITY_KEY)
    localStorage.removeItem(this.REAUTH_KEY)
    this.clearReauthFlagLocal()
    this.stopInactivityTimer()
  }

  /**
   * Obtiene el estado de actividad del usuario
   */
  getActivityStatus(): Observable<boolean> {
    return this.isActive.asObservable()
  }
}
